import React, { createContext } from 'react';

enum Permissions {
  // Clients
  CLIENT__INDEX = 'client:index',
  CLIENT__ARCHIVED = 'client:archived',
  CLIENT__CREATE = 'client:create',
  CLIENT__EDIT = 'client:edit',
  CLIENT__INVITE = 'client:invite',
  CLIENT__PROFILE = 'client:profile',
  CLIENT__PREVIEW_ASSETS = 'client:preview-assets',
  CLIENT__ONBOARD = 'client:onboard',
  CLIENT__TRANSFER = 'client:transfer',

  // Prospects
  PROSPECT__INDEX = 'prospect:index',
  PROSPECT__CREATE = 'prospect:create',
  PROSPECT__CONVERT_TO_CLIENT = 'prospect:convert-to-client',

  // Joint plans
  JOINT_PLAN__SHOW = 'joint-plan:show',
  JOINT_PLAN__DOWNLOAD_PDF = 'joint-plan:download-pdf',
  JOINT_PLAN__DELETE = 'joint-plan:delete',
  JOINT_PLAN__EDIT = 'joint-plan:edit',

  // Astute Requests
  ASTUTE_REQUEST__SHOW = 'astute-request:show',

  // Paraplanners
  PARAPLANNER__INDEX = 'paraplanner:index',
  PARAPLANNER__LINK = 'paraplanner:link',
  PARAPLANNER__UNLINK = 'paraplanners:unlink',

  // Notes
  NOTE__CREATE = 'note:create',
  NOTE__EDIT = 'note:edit',
  NOTE__VIEW_PRIVATE = 'note:view-private',

  // Customer support
  CUSTOMER_SUPPORT__QUICK_REPORT_ISSUE = 'customer-support:quick-report-issue',

  // Navigation permission based on models
  // The concept here is to tie the permission to the models, and then conditionally determine access on
  // the front end.

  // Home_all_access
  REMINDERS__INDEX = 'reminder:index',
  TASKS__INDEX = 'task:index',

  // Clients or prospects index permission
  // CLIENTS__INDEX = // 'clients:index',
  // PROSPECTS__INDEX = // 'prospects:index',
  // archive if clients or prospects

  // Organization
  ORGANIZATION__SHOW = 'organization:show',
  ROLE__INDEX = 'role:index',

  // Team _ new nav item per organisation role type
  USER_PROFILE__INDEX = 'user-profile:index',

  // Feature Toggle Permissions
  ASTUTE_CCP__ALL = 'astute-ccp:all',

  JOINT_PLAN__VIEW = 'joint-plan:view',
  JOINT_PLAN__CREATE = 'joint-plan:create',

  FNA__ALL = 'fna:all',

  NOTE_TEMPLATE__APPLY = 'note-template:apply',

  CLIENT_PDF_REPORT__ALL = 'client-pdf-report:all',

  CLIENT_LIABILITY__ALL = 'client-liability:all',

  ASSET__AUTO_LINKING = 'asset:auto-linking',

  ASSET_EVENT__ALL = 'asset-event:all',

  INVESTMENT_PROPOSAL__CREATE = 'investment-proposal:create',

  // FSP Admin
  FSP_ADMIN_DASHBOARD__SHOW = 'fsp-admin-dashboard:show',
  FSP_ADMIN_WEALTH_MANAGER__INDEX = 'fsp-admin-wealth-manager:index',
  FSP_ADMIN_WEALTH_MANAGER__CREATE = 'fsp-admin-wealth-manager:create',
  FSP_ADMIN_WEALTH_MANAGER__INVITE = 'fsp-admin-wealth-manager:invite',
  FSP_ADMIN_WEALTH_MANAGER__DEACTIVATE = 'fsp-admin-wealth-manager:deactivate',
  FSP_ADMIN_PARAPLANNER__INDEX = 'fsp-admin-paraplanner:index',
  FSP_ADMIN_PARAPLANNER__CREATE = 'fsp-admin-paraplanner:create',
  FSP_ADMIN_PARAPLANNER__INVITE = 'fsp-admin-paraplanner:invite',
  FSP_ADMIN_PARAPLANNER__DEACTIVATE = 'fsp-admin-paraplanner:deactivate',
  FSP_ADMIN_PARAPLANNER_ASSIGNMENT__INDEX = 'fsp-admin-paraplanner-assignment:index',
  FSP_ADMIN_PARAPLANNER_ASSIGNMENT__CREATE = 'fsp-admin-paraplanner-assignment:create',
  FSP_ADMIN_PARAPLANNER_ASSIGNMENT__UPDATE = 'fsp-admin-paraplanner-assignment:update',
  FSP_ADMIN_PARAPLANNER_ASSIGNMENT__DESTROY = 'fsp-admin-paraplanner-assignment:destroy',
  FSP_ADMIN_CLIENT__INDEX = 'fsp-admin-client:index',
  FSP_ADMIN_CLIENT__EXPORT = 'fsp-admin-client:export',
  FSP_ADMIN_CLIENT_TRANSFER__BULK_CREATE = 'fsp-admin-client-transfer:bulk-create'
}

type PermissionsKeys = keyof typeof Permissions;

type PermissionsContextType = {
  [key in Permissions]: boolean;
};

const initialPermissions: PermissionsContextType = {
  [Permissions['CLIENT__INDEX']]: false,
  [Permissions['CLIENT__ARCHIVED']]: false,
  [Permissions['CLIENT__CREATE']]: false,
  [Permissions['CLIENT__EDIT']]: false,
  [Permissions['CLIENT__INVITE']]: false,
  [Permissions['CLIENT__PROFILE']]: false,
  [Permissions['CLIENT__PREVIEW_ASSETS']]: false,
  [Permissions['CLIENT__ONBOARD']]: false,
  [Permissions['CLIENT__TRANSFER']]: false,
  [Permissions['PROSPECT__INDEX']]: false,
  [Permissions['PROSPECT__CREATE']]: false,
  [Permissions['PROSPECT__CONVERT_TO_CLIENT']]: false,
  [Permissions['JOINT_PLAN__SHOW']]: false,
  [Permissions['JOINT_PLAN__DOWNLOAD_PDF']]: false,
  [Permissions['JOINT_PLAN__DELETE']]: false,
  [Permissions['JOINT_PLAN__EDIT']]: false,
  [Permissions['ASTUTE_REQUEST__SHOW']]: false,
  [Permissions['PARAPLANNER__INDEX']]: false,
  [Permissions['PARAPLANNER__LINK']]: false,
  [Permissions['PARAPLANNER__UNLINK']]: false,
  [Permissions['NOTE__CREATE']]: false,
  [Permissions['NOTE__EDIT']]: false,
  [Permissions['NOTE__VIEW_PRIVATE']]: false,
  [Permissions['CUSTOMER_SUPPORT__QUICK_REPORT_ISSUE']]: false,
  [Permissions['REMINDER__INDEX']]: false,
  [Permissions['TASK__INDEX']]: false,
  // new but not implemented
  [Permissions['ORGANISATION__INDEX']]: false,
  [Permissions['ROLE__INDEX']]: false,
  [Permissions['USER_PROFILE__INDEX']]: false,

  // feature toggle permissions
  [Permissions['ASTUTE_CCP__ALL']]: false,
  [Permissions['JOINT_PLAN__VIEW']]: false,
  [Permissions['JOINT_PLAN__CREATE']]: false,
  [Permissions['FNA__ALL']]: false,
  [Permissions['NOTE_TEMPLATE__APPLY']]: false,
  [Permissions['CLIENT_PDF_REPORT__ALL']]: false,
  [Permissions['CLIENT_LIABILITIES__ALL']]: false,
  [Permissions['ASSET__AUTO_LINKING']]: false,
  [Permissions['ASSET_EVENTS__ALL']]: false,
  [Permissions['INVESTMENT_PROPOSAL__CREATE']]: false,
  [Permissions['FSP_ADMIN_DASHBOARD__SHOW']]: false,
  [Permissions['FSP_ADMIN_WEALTH_MANAGER__INDEX']]: false,
  [Permissions['FSP_ADMIN_WEALTH_MANAGER__CREATE']]: false,
  [Permissions['FSP_ADMIN_WEALTH_MANAGER__INVITE']]: false,
  [Permissions['FSP_ADMIN_WEALTH_MANAGER__DEACTIVATE']]: false,
  [Permissions['FSP_ADMIN_PARAPLANNER__INDEX']]: false,
  [Permissions['FSP_ADMIN_PARAPLANNER__CREATE']]: false,
  [Permissions['FSP_ADMIN_PARAPLANNER__INVITE']]: false,
  [Permissions['FSP_ADMIN_PARAPLANNER__DEACTIVATE']]: false,
  [Permissions['FSP_ADMIN_PARAPLANNER_ASSIGNMENT__INDEX']]: false,
  [Permissions['FSP_ADMIN_PARAPLANNER_ASSIGNMENT__CREATE']]: false,
  [Permissions['FSP_ADMIN_PARAPLANNER_ASSIGNMENT__UPDATE']]: false,
  [Permissions['FSP_ADMIN_PARAPLANNER_ASSIGNMENT__DESTROY']]: false,
  [Permissions['FSP_ADMIN_CLIENT__INDEX']]: false,
  [Permissions['FSP_ADMIN_CLIENT__EXPORT']]: false,
  [Permissions['FSP_ADMIN_CLIENT_TRANSFER__BULK_CREATE']]: false
};

export const PermissionsContext = createContext<PermissionsContextType>(initialPermissions);

type PermissionsProviderProps = {
  permissions: string[];
  featureTogglePermissions: string[],
  children: React.ReactNode;
};
const PermissionsProvider = ({
  permissions,
  featureTogglePermissions,
  children
}: PermissionsProviderProps) => {

  const _permissions = Array.from(new Set([...permissions, ...featureTogglePermissions]))

  const permissionsObject: PermissionsContextType = _permissions.reduce(
    (acc, permission) => {
      return {
        ...acc,
        [permission]: true,
      }
    },
    initialPermissions
  );

  return (
    <PermissionsContext.Provider value={permissionsObject}>
      {children}
    </PermissionsContext.Provider>
  );
};

export type { PermissionsContextType, PermissionsKeys }
export { Permissions }
export default PermissionsProvider;
